import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../redux/slices";
import { Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { ConfirmUnsavedChangesDialog } from "../common/ConfirmUnsavedChangesDialog";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { StepZero } from "./steps/StepZeroCreateLoc";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  CreateLocationStep,
  initialState,
  resetStateCreatLocUsers,
  setCreateLocationStep,
  setCreateUsersLoc,
} from "../../redux/slices/location/createLocation";
import StepTwoOptionB from "./steps/StepOneCreateLocB";

import StepTwoNoCreateUsers from "./steps/stepTwoCreateUser/StepTwoNoCreateUser";

import { useNavigate } from "react-router-dom";
import {
  fetchActionsNamesUsersConfig,
  fetchLocations,
} from "../../redux/actions/locations/locationsAsyncActions";
import { StepOneMultiLocations } from "./multilocation/StepOneMultiLoc";
import { StepThreeMultiLocations } from "./multilocation/StepThreeMultiLoc";
import { fetchGroups } from "../../redux/actions/users/userAsyncActions";
import { StepTwoMultiLocations } from "./multilocation/StepTwoMultiLoc";
import { GrainTrackerStock } from "../grainWise/create/GrainTrackerStock";
import {
  logoutGrainTracker,
  setTheBinIsGood,
} from "../../redux/slices/grainTracker/grainTracker";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    setSteps: (value: CreateLocationStep) => {
      dispatch(setCreateLocationStep(value));
    },

    cancel: (token: string) => {
      dispatch(resetStateCreatLocUsers());
      dispatch(logoutGrainTracker());
      dispatch(setTheBinIsGood(false));
      setTimeout(() => {
        dispatch(fetchLocations(token));
        dispatch(fetchGroups(token));
      }, 400);
    },

    fetchActionsNames: (token: string) => {
      dispatch(fetchActionsNamesUsersConfig(token));
    },

    fetchLocations: (token: string) => {
      dispatch(fetchLocations(token));
      dispatch(fetchGroups(token));
    },

    resetCreateUsersValues: () => {
      dispatch(setCreateUsersLoc(initialState.createUsers));
    },
  };
};

export interface MenuCreateLocationProps {
  createLocationStep: CreateLocationStep;
  t: (arg0: string) => string;
  setSteps: (value: CreateLocationStep) => void;
  multiLocationLoading: boolean;
  openConfirmDialog: boolean;
  handleCloseClick: () => void;
  handleConfirmClose: () => void;
  setOpenConfirmDialog: (value: boolean) => void;
}

export const MenuCreateLocation: FC<MenuCreateLocationProps> = ({
  createLocationStep,
  t,
  setSteps,
  multiLocationLoading,
  openConfirmDialog,
  handleCloseClick,
  handleConfirmClose,
  setOpenConfirmDialog,
}) => {
  const managingTitle = () => {
    if (createLocationStep.step === 0) {
      return t("Create_site.Create_site");
    }
    if (createLocationStep.step === 1) {
      return t("Create_site.Create_site");
    }
    if (createLocationStep.step === 2 && createLocationStep.option === "a") {
      return t("Create_site.Create_site");
    }
    if (createLocationStep.step === 3 && createLocationStep.option === "a") {
      return t("Create_site.Create_site");
    }
    if (createLocationStep.step === 2 && createLocationStep.option === "b") {
      return t("Create_site.Create_site");
    }
  };

  return (
    <>
      <div className="top-options-create-locations">
        <div className="spacer-div-back-button">
          {createLocationStep.step === 0 || createLocationStep.step === 2 ? (
            <div />
          ) : (
            <Button
              disabled={multiLocationLoading}
              sx={{
                fontSize: "14px",
                textTransform: "none",
                color: "#232128",
              }}
              onClick={() => {
                if (createLocationStep.place !== "configs") {
                  setSteps({
                    ...createLocationStep,
                    step: createLocationStep.step - 1,
                  });
                }
              }}
              startIcon={<ArrowBackIcon />}
              variant="text"
            >
              {t("Common_options.Back")}
            </Button>
          )}
        </div>
        <div className="create-loc-title">{managingTitle()}</div>
        <div>
          <Button
            disabled={multiLocationLoading}
            sx={{
              fontSize: "14px",
              textTransform: "none",
              color: "#232128",
            }}
            variant="text"
            endIcon={<CloseIcon />}
            onClick={handleCloseClick}
          >
            {t("Common_options.Close")}
          </Button>
        </div>
      </div>

      <ConfirmUnsavedChangesDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        t={t}
        confirmButtonLabel="Common_options.Close"
      />
    </>
  );
};

const CreateLocation: FC<any> = () => {
  const [t] = useTranslation("global");
  const { setSteps, cancel, fetchActionsNames, fetchLocations } = SetActions();
  const { createLocationStep, multiLocationLoading } = useSelector(
    (state: State) => state.CreateLocationReducer,
  );

  const {
    isCreatingWithGrainTracker,
    locationGrainTrackerValues,
    principalInfoGT,
    principalInfoGTCopy,
  } = useSelector((state: State) => state.GrainWiseReducer);
  const { user } = useSelector((state: State) => state.Authentication);
  const token = user?.token ?? "";

  const navigate = useNavigate();

  const { openConfirmDialog, setOpenConfirmDialog } = useUnsavedChanges({
    currentData: principalInfoGT,
    originalData: principalInfoGTCopy,
  });

  const handleCloseClick = () => {
    if (
      JSON.stringify(principalInfoGT) !== JSON.stringify(principalInfoGTCopy)
    ) {
      setOpenConfirmDialog(true);
    } else {
      navigate("/locations");
      setTimeout(() => {
        cancel(token);
      }, 500);
    }
  };

  const handleConfirmClose = () => {
    setOpenConfirmDialog(false);
    navigate("/locations");
    setTimeout(() => {
      cancel(token);
    }, 500);
  };

  useEffect(() => {
    fetchActionsNames(token);
    fetchLocations(token);
  }, []);

  return (
    <div className="create-sites-container">
      <MenuCreateLocation
        createLocationStep={createLocationStep}
        t={t}
        setSteps={setSteps}
        multiLocationLoading={multiLocationLoading}
        openConfirmDialog={openConfirmDialog}
        handleCloseClick={handleCloseClick}
        handleConfirmClose={handleConfirmClose}
        setOpenConfirmDialog={setOpenConfirmDialog}
      />
      {createLocationStep.step === 0 ? <StepZero /> : ""}
      {createLocationStep.step === 1 && createLocationStep.option === "a" ? (
        <StepOneMultiLocations />
      ) : (
        ""
      )}

      {createLocationStep.step === 2 && createLocationStep.option === "a" ? (
        <StepTwoMultiLocations />
      ) : (
        ""
      )}

      {createLocationStep.step === 3 && createLocationStep.option === "a" ? (
        <StepThreeMultiLocations />
      ) : (
        ""
      )}

      {createLocationStep.step === 1 && createLocationStep.option === "b" ? (
        <StepTwoOptionB />
      ) : (
        ""
      )}

      {createLocationStep.step === 2 &&
      createLocationStep.option === "b" &&
      isCreatingWithGrainTracker === true ? (
        <GrainTrackerStock />
      ) : (
        ""
      )}
      {createLocationStep.step === 2 && isCreatingWithGrainTracker === false ? (
        <StepTwoNoCreateUsers
          isCreatingWithGrainTracker={isCreatingWithGrainTracker}
          locationGrainTrackerValues={locationGrainTrackerValues}
        />
      ) : (
        ""
      )}

      {createLocationStep.step === 3 &&
      createLocationStep.option === "b" &&
      isCreatingWithGrainTracker === true ? (
        <StepTwoNoCreateUsers
          isCreatingWithGrainTracker={isCreatingWithGrainTracker}
          locationGrainTrackerValues={locationGrainTrackerValues}
        />
      ) : (
        ""
      )}
    </div>
  );
};

export default CreateLocation;
